import { useEffect, useState } from 'react'
import {
  attendanceGetSettings,
  attendanceSaveSettings,
  type AttendanceIpBuildingRow,
} from '../../lib/apiPhpAdapter'

type Props = {
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
  /** Bez vnějšího „card“ rámečku (např. uvnitř záložky personalisty). */
  embedded?: boolean
}

function isValidIp(ip: string): boolean {
  const t = ip.trim()
  if (!t) return false
  // IPv4 or IPv6 (basic)
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(t)) {
    return t.split('.').every((p) => {
      const n = Number(p)
      return Number.isInteger(n) && n >= 0 && n <= 255
    })
  }
  return t.includes(':') && t.length >= 3
}

export function AttendanceHoursSettingsPanel({ showAlert, embedded = false }: Props) {
  const [stravenkaMin, setStravenkaMin] = useState(300)
  const [stravenkaDraft, setStravenkaDraft] = useState('300')
  const [overtimeMode, setOvertimeMode] = useState<'bank' | 'pay'>('bank')
  const [nightFrom, setNightFrom] = useState('22:00')
  const [nightTo, setNightTo] = useState('06:00')
  const [lateGrace, setLateGrace] = useState('5')
  const [stravenkaRequirePlan, setStravenkaRequirePlan] = useState(true)
  const [codeWorked, setCodeWorked] = useState('001')
  const [codeStrav, setCodeStrav] = useState('STR')
  const [ipMap, setIpMap] = useState<AttendanceIpBuildingRow[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    attendanceGetSettings()
      .then((s) => {
        if (cancelled) return
        const m = Number(s.stravenka_min_minutes ?? 300)
        setStravenkaMin(m)
        setStravenkaDraft(String(m))
        const p = s.processing
        if (p) {
          setOvertimeMode(p.overtime_mode === 'pay' ? 'pay' : 'bank')
          setNightFrom(p.night_from || '22:00')
          setNightTo(p.night_to || '06:00')
          setLateGrace(String(p.late_grace_minutes ?? 5))
          setStravenkaRequirePlan(!!p.stravenka_require_plan)
          setCodeWorked(p.wage_codes?.worked || '001')
          setCodeStrav(p.wage_codes?.stravenky || 'STR')
        }
        setIpMap(
          Array.isArray(s.ip_building_map)
            ? s.ip_building_map.map((r) => ({
                ip: String(r.ip || ''),
                building_code: String(r.building_code || ''),
              }))
            : [],
        )
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          showAlert(e instanceof Error ? e.message : 'Nepodařilo se načíst nastavení hodin', 'error')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [showAlert])

  const saveStravenka = async () => {
    const n = parseInt(stravenkaDraft, 10)
    if (!Number.isFinite(n) || n < 0 || n > 24 * 60) {
      showAlert('Zadejte minuty 0–1440', 'error')
      return
    }
    setSaving(true)
    try {
      const res = await attendanceSaveSettings({ stravenka_min_minutes: n })
      const m = Number(res.stravenka_min_minutes ?? n)
      setStravenkaMin(m)
      setStravenkaDraft(String(m))
      showAlert(`Nárok na stravenku od ${m} minut odpracovaných`, 'success')
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uložení nastavení selhalo', 'error')
    } finally {
      setSaving(false)
    }
  }

  const saveProcessing = async () => {
    setSaving(true)
    try {
      await attendanceSaveSettings({
        processing: {
          overtime_mode: overtimeMode,
          night_from: nightFrom,
          night_to: nightTo,
          late_grace_minutes: parseInt(lateGrace, 10) || 0,
          stravenka_require_plan: stravenkaRequirePlan,
          wage_codes: { worked: codeWorked, stravenky: codeStrav },
        },
      })
      showAlert('Parametry zpracování uloženy', 'success')
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uložení selhalo', 'error')
    } finally {
      setSaving(false)
    }
  }

  const updateIpRow = (index: number, patch: Partial<AttendanceIpBuildingRow>) => {
    setIpMap((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  const addIpRow = () => {
    setIpMap((prev) => [...prev, { ip: '', building_code: '' }])
  }

  const removeIpRow = (index: number) => {
    setIpMap((prev) => prev.filter((_, i) => i !== index))
  }

  const saveIpMap = async () => {
    const cleaned = ipMap
      .map((r) => ({
        ip: r.ip.trim(),
        building_code: r.building_code.trim(),
      }))
      .filter((r) => r.ip !== '' || r.building_code !== '')

    for (const row of cleaned) {
      if (!isValidIp(row.ip)) {
        showAlert(`Neplatná IP adresa: ${row.ip || '(prázdná)'}`, 'error')
        return
      }
      if (!row.building_code) {
        showAlert(`U IP ${row.ip} chybí kód budovy`, 'error')
        return
      }
      if (row.building_code.length > 32) {
        showAlert(`Kód budovy u ${row.ip} je delší než 32 znaků`, 'error')
        return
      }
    }

    const seen = new Set<string>()
    for (const row of cleaned) {
      if (seen.has(row.ip)) {
        showAlert(`Duplicitní IP: ${row.ip}`, 'error')
        return
      }
      seen.add(row.ip)
    }

    setSaving(true)
    try {
      const res = await attendanceSaveSettings({ ip_building_map: cleaned })
      setIpMap(
        Array.isArray(res.ip_building_map)
          ? res.ip_building_map.map((r) => ({
              ip: String(r.ip || ''),
              building_code: String(r.building_code || ''),
            }))
          : cleaned,
      )
      showAlert('Mapování IP → budova uloženo', 'success')
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Uložení mapy IP selhalo', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={
        embedded
          ? undefined
          : {
              marginTop: '1rem',
              padding: '0.9rem 1rem',
              border: '1px solid rgba(13, 148, 136, 0.25)',
              borderRadius: 12,
              background: 'rgba(13, 148, 136, 0.06)',
            }
      }
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Stravenky</div>
      <p style={{ margin: '0 0 0.5rem', color: '#475569', fontSize: '0.85rem', lineHeight: 1.4 }}>
        Den se započítá do stravenek, pokud je odpracováno alespoň zadaný počet minut (výchozí 300 = 5 h).
        Započítání času událostí (např. lékař) nastavíte u jednotlivých položek v katalogu Události.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <label style={{ fontSize: '0.95rem' }}>
          Min. minut / den{' '}
          <input
            type="number"
            min={0}
            max={1440}
            value={stravenkaDraft}
            disabled={loading || saving}
            onChange={(e) => setStravenkaDraft(e.target.value)}
            style={{ width: 88, marginLeft: 6 }}
          />
        </label>
        <button
          type="button"
          disabled={loading || saving || String(stravenkaMin) === stravenkaDraft}
          onClick={() => void saveStravenka()}
        >
          Uložit práh
        </button>
      </div>

      <div style={{ fontWeight: 700, margin: '1.1rem 0 6px' }}>Parametry zpracování</div>
      <div style={{ display: 'grid', gap: 8, maxWidth: 520 }}>
        <label style={{ fontSize: '0.95rem' }}>
          Přesčas{' '}
          <select
            value={overtimeMode}
            disabled={loading || saving}
            onChange={(e) => setOvertimeMode(e.target.value === 'pay' ? 'pay' : 'bank')}
          >
            <option value="bank">Převádět do banky</option>
            <option value="pay">Nabízet k proplacení (export)</option>
          </select>
        </label>
        <label style={{ fontSize: '0.95rem' }}>
          Noční od{' '}
          <input
            type="time"
            value={nightFrom}
            disabled={loading || saving}
            onChange={(e) => setNightFrom(e.target.value)}
          />{' '}
          do{' '}
          <input type="time" value={nightTo} disabled={loading || saving} onChange={(e) => setNightTo(e.target.value)} />
        </label>
        <label style={{ fontSize: '0.95rem' }}>
          Povolený pozdní příchod (min){' '}
          <input
            type="number"
            min={0}
            max={120}
            value={lateGrace}
            disabled={loading || saving}
            onChange={(e) => setLateGrace(e.target.value)}
            style={{ width: 72 }}
            title="Tolerance oproti začátku směny — sestava Pozdní příchody"
          />
        </label>
        <p style={{ margin: 0, color: '#64748b', fontSize: '0.8rem' }}>
          Příchod do X minut po začátku směny se v sestavě „Pozdní příchody“ nepočítá jako pozdní.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.95rem' }}>
          <input
            type="checkbox"
            checked={stravenkaRequirePlan}
            disabled={loading || saving}
            onChange={(e) => setStravenkaRequirePlan(e.target.checked)}
          />
          Stravenka jen při směně dle kódu dne
        </label>
        <label style={{ fontSize: '0.9rem' }}>
          Kód odpracováno{' '}
          <input value={codeWorked} disabled={loading || saving} onChange={(e) => setCodeWorked(e.target.value)} />
        </label>
        <label style={{ fontSize: '0.9rem' }}>
          Kód stravenky{' '}
          <input value={codeStrav} disabled={loading || saving} onChange={(e) => setCodeStrav(e.target.value)} />
        </label>
        <button type="button" disabled={loading || saving} onClick={() => void saveProcessing()}>
          Uložit parametry
        </button>
      </div>

      <div style={{ fontWeight: 700, margin: '1.25rem 0 6px' }}>Podpis punchů podle IP</div>
      <p style={{ margin: '0 0 0.55rem', color: '#475569', fontSize: '0.85rem', lineHeight: 1.4 }}>
        Když zaměstnanec zaznamená příchod/odchod ze zařízení s uvedenou veřejnou IP, u záznamu se
        uloží zvolený kód budovy (např. K-29). Neznámá IP → kód <strong>X</strong>.
      </p>
      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <table style={{ width: '100%', maxWidth: 640, borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #cbd5e1' }}>
                Veřejná IP
              </th>
              <th style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #cbd5e1' }}>
                Kód budovy
              </th>
              <th style={{ width: 72, borderBottom: '1px solid #cbd5e1' }} aria-label="Akce" />
            </tr>
          </thead>
          <tbody>
            {ipMap.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ padding: '10px 6px', color: '#64748b' }}>
                  Žádné mapování — všechny self-punchy dostanou kód X.
                </td>
              </tr>
            ) : (
              ipMap.map((row, i) => (
                <tr key={`ip-map-${i}`}>
                  <td style={{ padding: '4px 6px' }}>
                    <input
                      value={row.ip}
                      disabled={loading || saving}
                      placeholder="např. 217.11.253.51"
                      onChange={(e) => updateIpRow(i, { ip: e.target.value })}
                      style={{ width: '100%', minWidth: 140, boxSizing: 'border-box' }}
                    />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input
                      value={row.building_code}
                      disabled={loading || saving}
                      placeholder="např. K-29"
                      maxLength={32}
                      onChange={(e) => updateIpRow(i, { building_code: e.target.value })}
                      style={{ width: '100%', minWidth: 100, boxSizing: 'border-box' }}
                    />
                  </td>
                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                    <button
                      type="button"
                      disabled={loading || saving}
                      onClick={() => removeIpRow(i)}
                      title="Odebrat"
                    >
                      Odebrat
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        <button type="button" disabled={loading || saving} onClick={addIpRow}>
          Přidat IP
        </button>
        <button type="button" disabled={loading || saving} onClick={() => void saveIpMap()}>
          Uložit mapování IP
        </button>
      </div>
    </div>
  )
}
